
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Privilege
 *
 * A privilege is an atomic access-control token that grants permission to a protected area of the interface or a specific class of data. Privileges are collected into roles, which are then assigned to users and groups to control what each user can see and do in the application.
 */
class Privilege extends Base implements EntityType {
    privilegeId: uuid;
    name: string;
    description?: string | null;

    /**
     * Create a new Privilege, with an (optional) JSON object or Privilege class
     *
     * @param {Object|Privilege} doc - Can be either a simple JSON object or a Privilege class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Privilege | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            privilegeId: genUUID(),
            name: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'privilege';
        // @ts-ignore
        this.privilegeId = doc._privilegeId || doc.privilegeId || doc.privilegeid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the privilegeId across all entity types
     */
    get id(): uuid {
        return this.privilegeId;
    }


    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique ID for a privilege"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.privilegeId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied Privilege entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Privilege[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'privilege',
            table: 'PRIVILEGES',
            comment: 'A privilege is an atomic access-control token that grants permission to a protected area of the interface or a specific class of data. Privileges are collected into roles, which are then assigned to users and groups to control what each user can see and do in the application.',
            fields: {
                privilegeId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique ID for a privilege'
                },
                name: {
                    type: 'string',
                    required: true,
                    comment: 'The name of the privilege'
                },
                description: {
                    type: 'string',
                    comment: 'Short description of the privilege'
                }
            }
        });
    }

}
export default Privilege;
